<script setup lang="ts">
  import AiAssistantBetaSidePanelView from '@/components/agent-actions/ai-assistant-beta/AiAssistantBetaSidePanelView.vue'
  import { useAiAssistantBeta } from '@/components/agent-actions/ai-assistant-beta/useAiAssistantBeta'
  import ChatMessageList from '@/components/app/chat/messages/ChatMessageList.vue'
  import DeleteChatDialog from '@/components/app/chat/components/DeleteChatDialog.vue'
  import FullscreenWelcome from '@/components/app/chat/components/FullscreenWelcome.vue'
  import RenameChatDialog from '@/components/app/chat/components/RenameChatDialog.vue'
  import SidePanelChatGreeting from '@/components/app/chat/sidepanel/SidePanelChatGreeting.vue'
  import SidePanelChatHeader from '@/components/app/chat/sidepanel/SidePanelChatHeader.vue'
  import SidePanelChatInput from '@/components/app/chat/sidepanel/SidePanelChatInput.vue'
  import SidePanelTabBar, {
    type SidePanelTab
  } from '@/components/app/chat/sidepanel/SidePanelTabBar.vue'
  import SidePanel from '@/components/ui/SidePanel.vue'
  import { useChatPdfExport } from '@/composables/useChatPdfExport'
  import { useChatRename } from '@/composables/useChatRename'
  import {
    useChatStream,
    persistContextData,
    loadPersistedContextData
  } from '@/composables/useChatStream'
  import { useChatSessionDocumentProcessing } from '@/composables/useDocumentProcessing'
  import { SESSION_OPTIMISTIC_REMOVED_EVENT } from '@/composables/useChatSessionHistory'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useSessionModelSync } from '@/composables/useSessionModelSync'
  import { useTranslation } from '@/composables/useTranslation'
  import { isFileSupported } from '@/constants/documentFileTypes'
  import { caseUuidFromAppCaseDetailRoute } from '@/utils/caseDetailRoute'
  import { hasCaseContextMessages } from '@/utils/chatCaseLock'
  import { computeContextAnnotationDelta, toChatContext } from '@/utils/chatContext'
  import { getSessionDisplayTitle } from '@/utils/chatSessionTitle'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { useUnifiedChatStore, resolveSidePanelLoginMarker } from '@/store/app/unifiedChat'
  import {
    AiChatQuestionTemplatesService,
    AiChatSessionsService,
    DocumentsService
  } from '@/types/generated'
  import type { AiChatQuestionTemplate } from '@/types/generated/types.gen'
  import { useToast } from '@/components/ui/toast'
  import { AIChatSessionTypeEnum } from '@/types/generated'
  import { storeToRefs } from 'pinia'
  import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'

  const route = useRoute()
  const unifiedChat = useUnifiedChatStore()
  const {
    isSidePanelOpen,
    sidePanelRouteSessionPinned,
    sidePanelPreferEmptySession,
    activeSidePanelSession,
    chatMode
  } = storeToRefs(unifiedChat)

  const isFullscreenOverlay = computed(() => chatMode.value === 'fullscreen-overlay')

  function notifySessionOptimisticallyRemoved(sessionUuid: string): void {
    if (typeof window === 'undefined') return
    window.dispatchEvent(
      new CustomEvent(SESSION_OPTIMISTIC_REMOVED_EVENT, { detail: { sessionUuid } })
    )
  }

  const userStore = useUserStore()
  const globalChatStore = useGlobalChatStore()

  // AI Assistant V2 Beta — adds a tab bar at the bottom of the side panel that swaps
  // between the existing chat and the new AI Assistant activation/suggestions view.
  // The tab is admin-only pre-activation, and visible to everyone once the org activates,
  // so non-admins never see the beta-announcement surface (per spec). When the gate is
  // false, the tab bar is hidden and the panel behaves exactly as before; defensive
  // watcher snaps the tab back to chat if the gate flips off mid-session.
  const { shouldShowSidePanelTab } = useAiAssistantBeta()
  const activeTab = ref<SidePanelTab>('chat')
  watch(shouldShowSidePanelTab, (allow) => {
    if (!allow) activeTab.value = 'chat'
  })

  watch(
    isFullscreenOverlay,
    (isOverlayOpen) => {
      unifiedChat.setTopNavOverride(isOverlayOpen ? 'ai-assistant' : 'none')
    },
    { immediate: true }
  )

  // Court database feature flag & toggle state
  const courtDatabaseFlag = useFeatureFlag('COURT_DATABASE')
  const courtDbEnabled = ref(false)

  const toggleCourtDb = async () => {
    const newValue = !courtDbEnabled.value
    courtDbEnabled.value = newValue

    const session = chat.activeSession.value
    if (!session) return // No session yet — local state is preserved; createSession will carry it

    try {
      await unifiedChat.updateSession(session.uuid!, { court_db_enabled: newValue })
    } catch {
      courtDbEnabled.value = !newValue
      toast({ title: 'Failed to update court database setting', variant: 'destructive' })
    }
  }

  const selectedModelId = ref<string | null>(null)
  const chat = useChatStream('sidepanel', { courtDbEnabled, model: selectedModelId })
  const { exportSessionPdf } = useChatPdfExport()

  /**
   * True when the panel should render the centered fullscreen welcome surface — the
   * empty state of the overlay layout, where ``FullscreenWelcome`` and
   * :class:`SidePanelChatInput` stack vertically as a single centered block (matching
   * the dedicated ``/app/ai-assistant`` route's empty layout).
   *
   * Drives both:
   *
   * - The welcome wrapper's ``v-if`` (mount the welcome + the teleport target slot).
   * - The composer's ``<Teleport :disabled>`` switch (move the composer DOM into the
   *   welcome slot when active, back to its bottom dock when inactive).
   *
   * The two consumers MUST share the same source of truth: a desync would either
   * leave the composer orphaned (welcome unmounted but teleport still pointed at
   * its missing slot) or render the composer twice (in both source and target).
   */
  const overlayWelcomeActive = computed(
    () =>
      chatMode.value === 'fullscreen-overlay' && !chat.hasMessages.value && !chat.streamError.value
  )

  // Sync courtDbEnabled when the active session changes (switch or clear).
  watch(
    () => chat.activeSession.value,
    (session) => {
      courtDbEnabled.value = session?.court_db_enabled ?? false
    }
  )
  useSessionModelSync(() => chat.activeSession.value, selectedModelId)

  // Provide session type for SaveSnippetDialog
  provide('sessionType', AIChatSessionTypeEnum.CASE)

  // Document processing
  const docProcessor = useChatSessionDocumentProcessing(chat.activeSession)
  const { toast } = useToast()

  /** Derive a file-type badge variant from a filename extension. */
  function fileVariant(filename: string): 'pdf' | 'word' | 'other' {
    const ext = filename.split('.').pop()?.toLowerCase() ?? ''
    if (ext === 'pdf') return 'pdf'
    if (['doc', 'docx'].includes(ext)) return 'word'
    return 'other'
  }

  /** Documents currently being uploaded — shown as badges in the input. */
  interface PendingInputDoc {
    id: string
    filename: string
    variant: 'pdf' | 'word' | 'other'
    loading: boolean
    uuid?: string
  }
  const pendingInputDocs = ref<PendingInputDoc[]>([])

  const uploadsInProgress = computed(() => pendingInputDocs.value.some((d) => d.loading))

  /**
   * UUIDs of documents uploaded but not yet sent in a message.
   * Cleaned up (deleted from the backend) if the user closes the panel,
   * starts a new chat, or switches sessions without sending.
   */
  const uncommittedDocUuids = ref<string[]>([])
  /**
   * Session UUID created only to host pending (unsent) uploads in the composer.
   * If abandoned before first send, the empty session is deleted.
   */
  const pendingContextSessionUuid = ref<string | null>(null)
  // --- Manual case context for the chat input ---
  const manualCase = ref<{ uuid: string; label: string } | null>(null)
  const caseAttaching = ref(false)
  const pendingCaseLabel = ref<string | null>(null)
  /** Docs uploaded since the last send — flushed into a context annotation on next send. */
  const pendingUploadedDocs = ref<Array<{ filename: string; uuid: string }>>([])

  /** Delete all uncommitted documents from the backend. Fire-and-forget. */
  function discardUncommittedDocs() {
    const uuids = uncommittedDocUuids.value.splice(0)
    for (const uuid of uuids) {
      DocumentsService.documentsDestroy({ uuid }).catch(() => {
        // Best-effort cleanup — if it fails the doc is orphaned but harmless
      })
    }
  }

  function hasConversationMessages(
    session: { messages?: Array<{ role?: string }> } | null
  ): boolean {
    if (!session?.messages) return false
    return session.messages.some(
      (message) => message.role === 'user' || message.role === 'assistant'
    )
  }

  function discardPendingContextSessionIfAbandoned(
    session: { uuid?: string; messages?: Array<{ role?: string }> } | null,
    fallbackUuid?: string
  ) {
    const sessionUuid = session?.uuid ?? fallbackUuid
    if (!sessionUuid || pendingContextSessionUuid.value !== sessionUuid) return
    if (session && hasConversationMessages(session)) {
      pendingContextSessionUuid.value = null
      return
    }
    pendingContextSessionUuid.value = null
    void unifiedChat.deleteSession(sessionUuid).catch(() => {
      // Best-effort cleanup — stale empty session is harmless if delete fails.
    })
    globalChatStore.removeChatSession(sessionUuid)
    notifySessionOptimisticallyRemoved(sessionUuid)
  }

  async function onUploadDocuments(files: File[]) {
    const supported = files.filter(isFileSupported)
    const unsupported = files.length - supported.length

    if (unsupported > 0) {
      toast({ title: `${unsupported} file(s) not supported`, variant: 'destructive' })
    }
    if (supported.length === 0) return

    // Ensure a session exists before uploading
    if (!chat.activeSession.value) {
      try {
        await unifiedChat.createSession(undefined, 'sidepanel')
        if (chat.activeSession.value?.uuid) {
          pendingContextSessionUuid.value = chat.activeSession.value.uuid
        }
      } catch {
        toast({ title: 'Failed to create session', variant: 'destructive' })
        return
      }
    }

    // Add badge entries immediately so the user sees them while uploading
    const entries: PendingInputDoc[] = supported.map((file) => ({
      id: crypto.randomUUID(),
      filename: file.name,
      variant: fileVariant(file.name),
      loading: true
    }))
    pendingInputDocs.value.push(...entries)

    // Prefill the input if the user hasn't typed anything yet
    const currentText = inputRef.value?.getMessage?.() ?? ''
    if (!currentText.trim()) {
      inputRef.value?.setMessage(t('analyseDocuments'))
    }

    // Upload each file and update badge state
    for (let i = 0; i < supported.length; i++) {
      try {
        const doc = await docProcessor.uploadDocument(supported[i])
        if (doc?.uuid) {
          uncommittedDocUuids.value.push(doc.uuid)
          const idx = pendingInputDocs.value.findIndex((d) => d.id === entries[i].id)
          if (idx >= 0) {
            pendingInputDocs.value[idx] = {
              ...pendingInputDocs.value[idx],
              loading: false,
              uuid: doc.uuid
            }
          }
          pendingUploadedDocs.value.push({
            filename: doc.filename || doc.title || supported[i].name,
            uuid: doc.uuid
          })
        } else {
          // Upload returned no uuid — treat as failure and remove the badge
          const idx = pendingInputDocs.value.findIndex((d) => d.id === entries[i].id)
          if (idx >= 0) pendingInputDocs.value.splice(idx, 1)
          toast({ title: `Upload failed for ${supported[i].name}`, variant: 'destructive' })
        }
      } catch {
        // Remove failed badge
        const idx = pendingInputDocs.value.findIndex((d) => d.id === entries[i].id)
        if (idx >= 0) pendingInputDocs.value.splice(idx, 1)
      }
    }
  }

  const caseUuidForFloatingChat = computed<string | null>(() =>
    caseUuidFromAppCaseDetailRoute(route)
  )

  /**
   * Bucket for route-driven session resolution: one key per case UUID, or ``global`` for all
   * non-case app pages (so intra-case tab changes do not clear a pinned thread).
   */
  const floatingRouteSessionKey = computed(() =>
    caseUuidForFloatingChat.value ? `case:${caseUuidForFloatingChat.value}` : 'global'
  )

  /**
   * Stable login marker for snapshot staleness detection.
   *
   * Resolved via the shared :func:`resolveSidePanelLoginMarker` helper so this auto-persist
   * watcher and the fullscreen collapse writer in :file:`AiChatMain.vue` stay in lock-step
   * (diverging rules would silently break the "collapse resumes same thread" UX on the edge
   * auth path where ``last_login`` is nullish).
   *
   * Returns ``undefined`` while the user store is still hydrating so that
   * ``readSidePanelSessionSnapshot`` defers instead of making a premature staleness decision.
   */
  const loginMarker = computed<string | undefined>(() => {
    if (!userStore.user) return undefined
    return resolveSidePanelLoginMarker(userStore.user) ?? undefined
  })

  const { t } = useTranslation({
    en: {
      routeSessionListFailed:
        'Could not load conversations for this page. You can still start a new chat or retry.',
      aiAssistant: 'AI assistant',
      showAll: 'Show all',
      analyseDocuments: 'Analyse the documents',
      readingDocuments: 'Reading documents...'
    },
    de: {
      routeSessionListFailed:
        'Konnte keine Unterhaltungen für diese Seite laden. Sie können trotzdem neu starten oder es erneut versuchen.',
      aiAssistant: 'KI-Assistent',
      showAll: 'Alles anzeigen',
      analyseDocuments: 'Analysiere die Dokumente',
      readingDocuments: 'Lese Dokumente...'
    },
    fr: {
      routeSessionListFailed:
        'Impossible de charger les conversations pour cette page. Vous pouvez quand même démarrer ou réessayer.',
      aiAssistant: 'Assistant IA',
      showAll: 'Tout afficher',
      analyseDocuments: 'Analyser les documents',
      readingDocuments: 'Lecture des documents...'
    }
  })

  let routeSessionSyncGeneration = 0

  /** Shown when the route-scoped session list request fails (panel stays usable). */
  const routeSessionListError = ref<string | null>(null)
  const headerRef = ref<InstanceType<typeof SidePanelChatHeader> | null>(null)
  const inputRef = ref<InstanceType<typeof SidePanelChatInput> | null>(null)
  const composerDraftSnapshot = ref('')

  function hasPendingComposerDraft(): boolean {
    const messageDraft = inputRef.value?.getMessage?.().trim() ?? ''
    return (
      messageDraft.length > 0 ||
      pendingInputDocs.value.length > 0 ||
      pendingUploadedDocs.value.length > 0 ||
      !!pendingCaseLabel.value ||
      caseAttaching.value
    )
  }

  /**
   * Load the latest session for the current route (by newest message), unless the user pinned a
   * thread or chose an empty “new chat” state.
   *
   * Within the same login session and calendar day the snapshot shortcut resumes the previous
   * thread for this route context. On a new day or after a fresh login the snapshot is stale and
   * the home/welcome screen is shown instead.
   *
   * Preserves in-flight streams across panel close/reopen on the same route. When a reply is
   * currently streaming for the session that the snapshot resolves to (i.e. nothing has changed
   * except the panel's open state), the cancel + reload path would (a) reset
   * ``streamingStatus`` to ``idle`` — re-enabling the send button while the backend still treats
   * the session as busy and rejects the next send with "Session is currently processing another
   * message." — and (b) replace ``messages.value`` with the persisted snapshot, dropping the
   * streaming placeholder (and the locally-pushed user message) so the thinking-wave loader
   * vanishes. Real route changes still land here with a mismatched ``snapshot.routeKey`` and
   * cancel + reload as before.
   */
  async function syncSidePanelSessionToRoute(): Promise<void> {
    if (!isSidePanelOpen.value) return
    if (sidePanelRouteSessionPinned.value) return
    if (hasPendingComposerDraft()) return
    // "Prefer empty new chat" is only for non-case pages. Case routes should keep
    // the existing route-based auto selection behavior.
    if (sidePanelPreferEmptySession.value && floatingRouteSessionKey.value === 'global') return

    const routeKey = floatingRouteSessionKey.value
    const { snapshot, stale } = unifiedChat.readSidePanelSessionSnapshot(loginMarker.value)

    const activeUuid = chat.activeSession.value?.uuid
    const snapshotMatchesActive =
      !stale && !!activeUuid && snapshot?.routeKey === routeKey && snapshot?.uuid === activeUuid

    if (chat.isStreaming.value && snapshotMatchesActive) {
      routeSessionListError.value = null
      return
    }

    const gen = ++routeSessionSyncGeneration
    chat.cancelStreamingForRouteSwitch()

    // New day or new login → show home/welcome screen instead of resuming.
    if (stale) {
      if (chat.activeSession.value) {
        chat.clearChat()
      }
      return
    }

    // Snapshot is valid (same day + same login) and matches the current route → resume thread.
    if (snapshot?.routeKey === routeKey) {
      const ok = await chat.loadSession(snapshot.uuid)
      if (gen !== routeSessionSyncGeneration) {
        return
      }
      if (ok) {
        routeSessionListError.value = null
        return
      }
      // Session no longer exists – clear stale snapshot and fall through to API lookup.
      unifiedChat.clearSidePanelSessionSnapshot()
    }

    // No snapshot for this route context – look up the latest session via API.
    try {
      const caseUuid = caseUuidForFloatingChat.value
      const page = await AiChatSessionsService.aiChatSessionsList({
        floatingCaseUuid: caseUuid ?? undefined,
        floatingGlobal: caseUuid ? undefined : true,
        ordering: '-last_message_at',
        limit: 1
      })
      if (gen !== routeSessionSyncGeneration) return

      routeSessionListError.value = null

      const first = page.results?.[0]
      const currentUuid = chat.activeSession.value?.uuid ?? null

      if (first?.uuid) {
        if (first.uuid !== currentUuid) {
          await chat.loadSession(first.uuid)
        }
      } else if (currentUuid) {
        chat.clearChat()
      }
    } catch (err) {
      if (gen !== routeSessionSyncGeneration) return
      routeSessionListError.value = t('routeSessionListFailed')
      if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
        console.error('[SidePanelChat] route session list failed', err)
      }
    }
  }

  watch(floatingRouteSessionKey, () => {
    routeSessionListError.value = null
    unifiedChat.clearSidePanelRouteSessionPin()
    void syncSidePanelSessionToRoute()
  })

  watch(
    isSidePanelOpen,
    (open) => {
      if (open) {
        void syncSidePanelSessionToRoute()
      }
    },
    { immediate: true }
  )

  watch(
    () =>
      [
        isSidePanelOpen.value,
        activeSidePanelSession.value?.uuid,
        floatingRouteSessionKey.value,
        loginMarker.value
      ] as const,
    ([open, sessionUuid, key, marker]) => {
      if (!open || !sessionUuid || !marker) {
        return
      }
      unifiedChat.persistSidePanelSessionSnapshot(sessionUuid, key, marker)
    }
  )

  const firstName = computed(() => userStore.user?.first_name ?? '')
  const lastName = computed(() => userStore.user?.last_name ?? '')

  const allTemplates = ref<AiChatQuestionTemplate[]>([])

  onMounted(async () => {
    courtDatabaseFlag.checkFlag()
    document.addEventListener('keydown', onKeydown)
    unifiedChat.fetchAvailableModels()
    try {
      const res = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesList({ limit: 0 })
      allTemplates.value = res.results ?? []
    } catch {
      // silently fail – greeting will just show no suggestions
    }
  })

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown)
    unifiedChat.setTopNavOverride('none')
  })

  const topTemplates = computed(() => {
    const templates = allTemplates.value
    const favorites = templates.filter((t) => t.is_favorite === 'true')

    if (favorites.length >= 4) {
      // Among favorites, prioritize Jupus templates
      const jupusFavs = favorites.filter((t) => t.is_jupus_template)
      const otherFavs = favorites.filter((t) => !t.is_jupus_template)
      return [...jupusFavs, ...otherFavs].slice(0, 4)
    }

    // Not enough favorites – fill from all templates, Jupus first
    const jupus = templates.filter((t) => t.is_jupus_template)
    const rest = templates.filter((t) => !t.is_jupus_template)
    return [...jupus, ...rest].slice(0, 4)
  })

  const contextCaseLabel = computed(() => {
    const c = unifiedChat.currentContextCase
    if (!c) return null
    return c.case_title ?? c.jupus_case_number ?? c.uuid ?? null
  })

  const inputContextLabel = computed(() =>
    unifiedChat.hasContextCase ? contextCaseLabel.value : null
  )

  /**
   * Optimistic lock flag flipped on the first local send after attaching a case, used to
   * cover the window between ``onSend`` and the backend round-trip that refreshes
   * ``session.messages``. Do **not** read this directly for the UI lock — use
   * :const:`caseLocked`, which also consults the authoritative session payload so the lock
   * survives reloads and side-panel → fullscreen transitions without relying on local
   * message state that may not have been populated yet when the session watcher fires.
   */
  const caseSentWithContext = ref(false)

  /**
   * Whether the attached case is pinned to the session (remove affordance must be hidden).
   * Mirrors the backend detach rule in :func:`assert_case_detach_allowed`: any user or
   * assistant message recorded at or after ``case_linked_at`` locks the case. Falls back to
   * the local ``caseSentWithContext`` flag for the narrow window between a first send and
   * the API refresh that repopulates ``session.messages``.
   */
  const caseLocked = computed(() => {
    const session = chat.activeSession.value
    if (!session?.case_linked_at) return false
    if (caseSentWithContext.value) return true
    return hasCaseContextMessages(session)
  })

  // Unified case badge: manual selection takes priority, then route-based context
  const activeCaseLabel = computed(() => {
    if (manualCase.value) return manualCase.value.label
    if (inputContextLabel.value) return inputContextLabel.value
    return null
  })

  const hasActiveCase = computed(() => !!manualCase.value || !!inputContextLabel.value)

  const inputBadges = computed(() => {
    type BadgeItem = {
      id: string
      label: string
      variant: 'case' | 'pdf' | 'word' | 'other' | 'court-db'
      removable?: boolean
      loading?: boolean
    }
    const result: BadgeItem[] = []

    // Document badges (pending uploads shown in input)
    for (const doc of pendingInputDocs.value) {
      result.push({
        id: doc.id,
        label: doc.filename,
        variant: doc.variant,
        removable: !doc.loading,
        loading: doc.loading
      })
    }

    // The case badge only renders while the case is still pre-send. As soon as the user
    // sends the first message in case context, ``caseLocked`` flips to ``true`` and the
    // badge is dropped from the input row (the case remains attached to the session via
    // ``case_uuid`` — only the visual label is cleared, mirroring how document badges are
    // cleared on send). The court-database badge is intentionally NOT gated on
    // ``caseLocked`` because the verdict toggle persists across messages by design.
    if (!caseLocked.value) {
      if (caseAttaching.value && pendingCaseLabel.value) {
        result.push({
          id: '__case__',
          label: pendingCaseLabel.value,
          variant: 'case',
          removable: false,
          loading: true
        })
      } else if (activeCaseLabel.value) {
        result.push({
          id: '__case__',
          label: activeCaseLabel.value,
          variant: 'case',
          removable: true
        })
      }
    }
    return result
  })

  async function onSelectCase(uuid: string, label: string) {
    caseAttaching.value = true
    pendingCaseLabel.value = label
    try {
      // Load case data into the store (for AI context) — does NOT persist to session yet.
      await unifiedChat.setContextCase(uuid)
      manualCase.value = { uuid, label }
    } catch {
      unifiedChat.clearContextCase()
      toast({ title: 'Failed to attach case', variant: 'destructive' })
    } finally {
      caseAttaching.value = false
      pendingCaseLabel.value = null
    }
  }

  /** Persist the case link to the backend session. Called once on first send with case context. */
  async function commitCaseToSession() {
    const session = chat.activeSession.value
    const mc = manualCase.value
    if (!session || !mc) return
    try {
      await unifiedChat.updateSession(session.uuid, { case_uuid: mc.uuid })
      persistContextData(session.uuid, { caseLabel: mc.label, caseUuid: mc.uuid })
    } catch {
      // Non-fatal — the context annotation is already in the thread
    }
  }

  function onRemoveCase() {
    // Case hasn't been sent yet (caseLocked prevents removal after send),
    // so just clear local state — nothing to undo on the backend.
    unifiedChat.clearContextCase()
    manualCase.value = null
  }

  function onRemoveBadge(id: string) {
    if (id === '__case__') {
      onRemoveCase()
      return
    }
    // Remove a pending document badge
    const docIdx = pendingInputDocs.value.findIndex((d) => d.id === id)
    if (docIdx >= 0) {
      const removed = pendingInputDocs.value[docIdx]
      pendingInputDocs.value.splice(docIdx, 1)
      // Also remove from pendingUploadedDocs and delete from backend if already uploaded
      if (removed.uuid) {
        const uploadIdx = pendingUploadedDocs.value.findIndex((d) => d.uuid === removed.uuid)
        if (uploadIdx >= 0) pendingUploadedDocs.value.splice(uploadIdx, 1)
        const uncommitIdx = uncommittedDocUuids.value.indexOf(removed.uuid)
        if (uncommitIdx >= 0) uncommittedDocUuids.value.splice(uncommitIdx, 1)
        DocumentsService.documentsDestroy({ uuid: removed.uuid }).catch(() => {})
      }
    }
  }

  /**
   * Inject a context banner for the **delta** the user is sending this turn.
   *
   * Each call appends a fresh banner just above the upcoming user message
   * (see :func:`useChatStream.injectContextMessage`). To avoid duplicating
   * earlier turns' content we strip:
   *
   * - documents whose ``uuid`` is already present in any prior banner — only
   *   newly-uploaded docs end up in the new banner;
   * - the case label/uuid when the case has already been announced in an
   *   earlier banner — the case is single per session, so it appears in
   *   exactly one banner (the turn it was attached).
   *
   * The result is one banner per attach turn, anchored to the message that
   * was actually sent with that context — instead of every banner accumulating
   * the full session state and pinning to the earliest message.
   *
   * :param opts.caseLabel: Currently attached case label, if any. Ignored when
   *     a previous banner already carried a case label.
   * :param opts.caseUuid: Matching case uuid for ``caseLabel``.
   * :param opts.extraDocuments: Docs uploaded since the last send. Already
   *     known docs (from prior banners) are filtered out.
   * :return: None.
   */
  function updateContextAnnotation(opts?: {
    caseLabel?: string
    caseUuid?: string
    extraDocuments?: Array<{ filename: string; uuid: string }>
  }) {
    const session = chat.activeSession.value
    const sessionDocs = (session?.documents ?? [])
      .filter((d) => !d.is_deleted)
      .map((d) => ({ filename: d.filename || d.title, uuid: d.uuid }))

    const previouslyAnnotatedDocUuids = new Set<string>()
    let caseAlreadyAnnotated = false
    for (const m of chat.messages.value) {
      if (m.role !== 'context' || !m.contextData) continue
      if (m.contextData.caseLabel) caseAlreadyAnnotated = true
      for (const d of m.contextData.documents ?? []) {
        previouslyAnnotatedDocUuids.add(d.uuid)
      }
    }

    const delta = computeContextAnnotationDelta({
      sessionDocuments: sessionDocs,
      extraDocuments: opts?.extraDocuments ?? [],
      caseLabel: opts?.caseLabel ?? manualCase.value?.label ?? activeCaseLabel.value ?? undefined,
      caseUuid: opts?.caseUuid ?? manualCase.value?.uuid ?? undefined,
      previouslyAnnotatedDocUuids,
      caseAlreadyAnnotated
    })

    if (!delta) return

    chat.injectContextMessage(delta)
  }

  // Restore or clear manual case when switching sessions.
  watch(
    () => chat.activeSession.value,
    (session, _old) => {
      const oldUuid = _old?.uuid
      const newUuid = session?.uuid

      if (!newUuid) {
        discardPendingContextSessionIfAbandoned(_old ?? null, oldUuid ?? undefined)
        manualCase.value = null
        caseSentWithContext.value = false
        pendingUploadedDocs.value = []
        pendingInputDocs.value = []
        return
      }

      if (oldUuid && newUuid !== oldUuid) {
        discardPendingContextSessionIfAbandoned(_old ?? null, oldUuid ?? undefined)
        caseSentWithContext.value = false
        pendingUploadedDocs.value = []
        pendingInputDocs.value = []
      }

      // Restore manualCase from the session's API fields if this session has a linked case
      // but we don't have the manual case set yet (e.g. after a page refresh).
      // Skip when caseAttaching is true — we're inside onSelectCase and will set manualCase ourselves.
      //
      // Note: we no longer mirror ``chat.hasMessages`` into ``caseSentWithContext`` here —
      // local messages may not be populated yet when the activeSession watcher fires
      // (``loadSession`` sets ``activeSession`` inside its awaited body and assigns
      // ``messages.value`` after we resume, so watchers can flush between the two
      // updates). The lock now derives from ``session.messages`` via
      // :func:`hasCaseContextMessages`, which does not suffer from that race.
      if (session?.case_linked_at && !manualCase.value && !caseAttaching.value) {
        const persisted = loadPersistedContextData(newUuid)
        const label = session.case_title ?? persisted?.caseLabel
        const uuid = session.case_uuid ?? persisted?.caseUuid
        if (label) {
          manualCase.value = { uuid: uuid ?? '', label }
        }
      } else if (oldUuid && newUuid !== oldUuid) {
        manualCase.value = null
      }
    }
  )

  const onClose = () => {
    discardPendingContextSessionIfAbandoned(chat.activeSession.value)
    discardUncommittedDocs()
    pendingInputDocs.value = []
    pendingUploadedDocs.value = []
    unifiedChat.closeSidePanel()
  }

  /**
   * Reshape the floating side panel into a full-viewport overlay that lays out like the
   * fullscreen ``/app/ai-assistant`` view, **without** unmounting any chat surface.
   *
   * The expand button used to ``router.push`` to ``/app/ai-assistant`` and hand the
   * thread off to a parallel chat stack (``AiChat.vue`` + ``globalChatStore.activeChatSession``
   * + a fresh WebSocket subscription). That hop caused three user-visible regressions:
   *
   * 1. A full-page reload feel — the underlying page fully unmounted.
   * 2. Mid-stream switching was impossible because the destination remounted the chat.
   * 3. Document/case banner ordering drifted between the two surfaces because each one
   *    has its own (incompatible) banner reconstruction code.
   *
   * Flipping ``chatMode`` to ``'fullscreen-overlay'`` keeps the same component tree
   * (``SidePanelChat`` + the ``useChatStream('sidepanel')`` instance + the same WebSocket
   * subscription) and only changes the layout via CSS class swaps — no remount, no refetch,
   * no banner regroup, streaming continues across the transition.
   *
   * The dedicated ``/app/ai-assistant`` route remains reachable from the topnav for users
   * who arrive directly via URL or bookmark; this expand path simply no longer routes there.
   *
   * :return: None.
   */
  const onExpand = () => {
    composerDraftSnapshot.value = inputRef.value?.getMessage?.() ?? ''
    unifiedChat.setChatMode('fullscreen-overlay')
    nextTick(() => {
      if (composerDraftSnapshot.value && !(inputRef.value?.getMessage?.() ?? '').trim()) {
        inputRef.value?.setMessage(composerDraftSnapshot.value)
      }
      inputRef.value?.focus()
    })
  }

  const onCollapse = () => {
    composerDraftSnapshot.value = inputRef.value?.getMessage?.() ?? ''
    unifiedChat.setChatMode('sidepanel')
    nextTick(() => {
      if (composerDraftSnapshot.value && !(inputRef.value?.getMessage?.() ?? '').trim()) {
        inputRef.value?.setMessage(composerDraftSnapshot.value)
      }
      inputRef.value?.focus()
    })
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return
    if (!isSidePanelOpen.value) return
    if (showDeleteDialog.value || showRenameDialog.value) return

    if (isFullscreenOverlay.value) {
      onCollapse()
    } else {
      onClose()
    }
  }

  function onNewChat() {
    discardPendingContextSessionIfAbandoned(chat.activeSession.value)
    discardUncommittedDocs()
    pendingInputDocs.value = []
    pendingUploadedDocs.value = []
    chat.clearChat()
    // clearChat already nulls the active session and clears the snapshot.
    // Only set the two flags that startNewChat would additionally provide.
    unifiedChat.setSidePanelPreferEmptySession(true)
    unifiedChat.clearSidePanelRouteSessionPin()
  }

  function onSelectSession(uuid: string) {
    discardPendingContextSessionIfAbandoned(chat.activeSession.value)
    discardUncommittedDocs()
    unifiedChat.pinSidePanelRouteSession()
    unifiedChat.setSidePanelPreferEmptySession(false)
    void chat.loadSession(uuid).then(() => inputRef.value?.focus())
  }

  function onSuggestion(uuid: string) {
    const template = topTemplates.value.find((t) => t.uuid === uuid)
    if (template) {
      inputRef.value?.setMessage(template.prompt)
    }
  }

  /**
   * Prefill the composer with a quick-action prompt emitted by :class:`FullscreenWelcome`.
   *
   * The fullscreen welcome chips emit the prompt **string** directly (the side-panel
   * greeting emits a template UUID instead, since it can render up to four templates and
   * needs to look up the matching prompt). We deliberately do NOT auto-send here — staying
   * consistent with :func:`onSuggestion` keeps the user in control and lets them edit the
   * prompt before submitting, just like clicking a suggestion in the docked side panel.
   *
   * :param prompt: Raw prompt string to drop into the composer.
   * :return: None.
   */
  function onFullscreenWelcomeSuggestion(prompt: string) {
    if (!prompt) return
    inputRef.value?.setMessage(prompt)
  }

  function onShowAll() {
    headerRef.value?.openModalWithTab('prompts')
  }

  /**
   * True when this turn introduces context that has not yet been shown in a
   * banner.
   *
   * Each call to ``updateContextAnnotation`` appends a fresh banner above the
   * message about to be sent (the previous in-place-update behavior was the
   * source of the "doc B lands above msg 1" bug). We trigger the call when:
   *
   * - Any document has been uploaded since the last send (``hasPendingDocs``).
   * - A case is attached that has never been announced in a banner yet —
   *   regardless of whether earlier banners already exist for documents.
   *
   * The earlier guard skipped the case-only path when a doc-only banner
   * already existed, leaving a mid-conversation case attach silently
   * un-annotated.
   */
  const contextAnnotationPending = computed(() => {
    const hasPendingDocs = pendingUploadedDocs.value.length > 0
    const hasNewCase = hasActiveCase.value && !caseLocked.value
    const caseAlreadyAnnotated = chat.messages.value.some(
      (m) => m.role === 'context' && m.contextData?.caseLabel
    )

    if (hasPendingDocs) return true
    if (hasNewCase && !caseAlreadyAnnotated) return true
    return false
  })

  /** True when the last send included documents that may still be processing. */
  const sentWithDocuments = ref(false)

  async function onSend(message: string) {
    // Docs are now committed — don't delete them on close/new-chat
    uncommittedDocUuids.value = []
    const hadDocs = pendingUploadedDocs.value.length > 0

    // If there's pending context (newly attached case or uploaded docs), inject the
    // annotation into the chat thread now — right before the user message.
    if (contextAnnotationPending.value) {
      updateContextAnnotation({
        caseLabel: activeCaseLabel.value ?? undefined,
        caseUuid: manualCase.value?.uuid ?? caseUuidForFloatingChat.value ?? undefined,
        extraDocuments: pendingUploadedDocs.value
      })
      pendingUploadedDocs.value = []
    }

    // Lock the case after sending with it attached, and persist to backend.
    //
    // ``commitCaseToSession`` MUST be awaited before ``chat.sendMessage``: the send
    // travels via WebSocket while the case-link travels via REST PATCH. WS arrives
    // at the backend in ~1ms while a fresh PATCH takes 50–200ms, so without the
    // await the backend records the new user message **before** ``case_linked_at``
    // is set. The fullscreen view (``AiChatMain``) reconstructs context banners by
    // matching ``case_linked_at`` against ``messages[*].created_at`` — when the
    // case timestamp is later than the new user message but earlier than the
    // assistant reply, the banner snaps below the last message instead of above
    // the user message that triggered the attach. Awaiting the PATCH guarantees
    // ``case_linked_at < user_msg.created_at`` so banners land in the right slot
    // when the user expands the side panel into fullscreen or reloads the page.
    const needsCaseCommit = hasActiveCase.value && !caseSentWithContext.value
    if (needsCaseCommit) {
      caseSentWithContext.value = true
      await commitCaseToSession()
    }
    pendingContextSessionUuid.value = null

    // Clear document badges from the input
    pendingInputDocs.value = []
    sentWithDocuments.value = hadDocs

    chat.sendMessage(message)
  }

  // Reset sentWithDocuments when streaming completes
  watch(
    () => chat.isStreaming.value,
    (streaming) => {
      if (!streaming) sentWithDocuments.value = false
    }
  )

  /** Label for the streaming loader when documents are being processed after send. */
  const streamingLabel = computed(() => {
    if (sentWithDocuments.value && chat.isStreaming.value) {
      return t('readingDocuments')
    }
    return undefined
  })

  /**
   * Template ref to the centered welcome's composer slot. Used as the ``<Teleport>``
   * target instead of a CSS selector — Vue's compiler patches the teleport's update
   * out-of-source-order with the wrapper's ``v-if`` mount, so a ``document.querySelector``
   * lookup at teleport-update time can still see ``null``. A reactive HTMLElement
   * ref guarantees the teleport only activates after Vue has actually written the
   * slot's DOM node, eliminating the ``insertBefore`` on ``null`` race.
   */
  const overlayComposerSlotEl = ref<HTMLDivElement | null>(null)

  /**
   * Restore composer focus whenever the teleport target changes.
   *
   * Vue's ``<Teleport>`` moves the rendered DOM via native ``appendChild`` /
   * ``removeChild`` calls. Modern browsers drop focus from a focused element when it
   * is reparented this way (the composer's ``<textarea>`` is the same DOM node, but
   * ``document.activeElement`` is reset to ``<body>``). Without this watcher, two
   * common transitions strand the cursor:
   *
   * - **Send-while-overlaid**: user types in the welcome-state composer, hits send →
   *   ``hasMessages`` flips → composer teleports back to the bottom dock → focus
   *   would be lost mid-stream, forcing the user to click the textarea again to
   *   type a follow-up.
   * - **Mid-empty-state collapse**: user expands, types, collapses → composer
   *   teleports back to source → focus would land on ``<body>``.
   *
   * The explicit ``focus()`` calls in :func:`onExpand` / :func:`onCollapse` cover
   * the click-driven transitions, but neither runs for content-driven moves
   * (e.g. ``hasMessages`` flipping on send), so we anchor the restore on the same
   * computed that gates the teleport itself. ``nextTick`` ensures the DOM move has
   * already happened before we ask the textarea to take focus back.
   */
  watch(
    () => overlayWelcomeActive.value,
    () => {
      void nextTick(() => inputRef.value?.focus())
    }
  )

  function onOpenPrompts() {
    headerRef.value?.openModalWithTab('prompts')
  }

  function onUsePrompt(prompt: string) {
    inputRef.value?.setMessage(prompt)
  }

  // --- Download PDF ---
  function onDownloadPdf() {
    const uuid = chat.activeSession.value?.uuid
    if (uuid) exportSessionPdf(uuid)
  }

  // --- Rename ---
  const showRenameDialog = ref(false)

  /**
   * Side-panel rename pipeline. Delegates optimistic update, global-store
   * mirror, rollback and destructive toast to the shared :func:`useChatRename`
   * composable; the ``onAfterCommit`` hook bypasses the side-panel header
   * popover cooldown so the renamed title is reflected immediately.
   */
  const { renameSession: renameSidePanelSession } = useChatRename({
    onAfterCommit: () => {
      headerRef.value?.refreshSessions()
    }
  })

  async function onRenameConfirmed(title: string) {
    const session = chat.activeSession.value
    if (!session?.uuid) return
    const outcome = await renameSidePanelSession(session, title)
    if (outcome.status === 'committed') {
      toast({ title: t('aiAssistant') })
    }
  }

  // --- Organize (reuse ChatHistoryModal folders tab in move mode) ---
  const moveSessionTitle = ref<string | null>(null)
  const moveSessionUuid = ref<string | null>(null)

  function onOrganize() {
    const session = chat.activeSession.value
    if (!session) return
    moveSessionTitle.value = getSessionDisplayTitle(session) || null
    moveSessionUuid.value = session.uuid
    headerRef.value?.openModalWithTab('folders')
  }

  function clearMoveState() {
    moveSessionTitle.value = null
    moveSessionUuid.value = null
  }

  async function onMoveToFolder(data: { sessionUuid: string; folderUuid: string }) {
    try {
      await unifiedChat.updateSession(data.sessionUuid, {
        session_group_uuid: data.folderUuid
      })
    } catch {
      // global error handler
    } finally {
      clearMoveState()
    }
  }

  // --- Delete ---
  const showDeleteDialog = ref(false)

  async function onConfirmDelete() {
    const uuid = chat.activeSession.value?.uuid
    if (!uuid) return
    try {
      await unifiedChat.deleteSession(uuid)
      // ``unifiedChat.deleteSession`` only clears the unified store slots — the
      // legacy ``globalChatStore.chatSessions`` list (used by the sidebar / case
      // chat selector / fullscreen popover fallback) would otherwise keep a
      // stale entry for this uuid. Mirror the removal explicitly so every
      // surface sees the session disappear in one tick.
      globalChatStore.removeChatSession(uuid)
      chat.clearChat()
      // Bypass the history popover's refresh cooldown so the deleted session is
      // evicted from the list the next time the user opens the popover.
      headerRef.value?.refreshSessions()
    } catch {
      // global error handler
    } finally {
      showDeleteDialog.value = false
    }
  }
</script>

<template>
  <SidePanel
    :model-value="unifiedChat.isSidePanelOpen"
    :overlay="isFullscreenOverlay"
    :aria-label="t('aiAssistant')"
    data-cy="ai-side-panel"
  >
    <!--
      Keep the chat tree mounted (not ``v-if``) so swapping to the AI Assistant tab and
      back preserves local state — input draft, scroll position, and any pending
      ``inputRef.setMessage(...)`` call survive the tab swap.

      We use a class-based ``hidden`` toggle instead of ``v-show`` because the Tailwind
      config sets ``important: true``, which makes the ``flex`` utility emit
      ``display: flex !important`` — that beats ``v-show``'s inline ``display: none`` and
      the chat div stays visible even when it should be hidden.
    -->
    <div
      :class="[
        'flex flex-1 flex-col self-stretch overflow-hidden',
        { hidden: activeTab !== 'chat' }
      ]"
    >
      <SidePanelChatHeader
        ref="headerRef"
        :session-title="getSessionDisplayTitle(chat.activeSession.value)"
        :has-active-session="chat.hasMessages.value || !!chat.activeSession.value"
        :has-messages="chat.hasMessages.value"
        :is-streaming="chat.isStreaming.value"
        :is-fullscreen-overlay="isFullscreenOverlay"
        :move-session-title="moveSessionTitle"
        :move-session-uuid="moveSessionUuid"
        @close="onClose"
        @expand="onExpand"
        @collapse="onCollapse"
        @new-chat="onNewChat"
        @select-session="onSelectSession"
        @use-prompt="onUsePrompt"
        @download-pdf="onDownloadPdf"
        @rename="showRenameDialog = true"
        @organize="onOrganize"
        @delete-chat="showDeleteDialog = true"
        @move-to-folder="onMoveToFolder"
        @move-cancelled="clearMoveState"
      />

      <div
        v-if="routeSessionListError"
        class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        :class="isFullscreenOverlay ? 'mx-auto mt-2 w-full max-w-4xl px-0' : 'mx-4 mt-2'"
        role="alert"
        data-cy="ai-side-panel-route-session-error"
      >
        {{ routeSessionListError }}
      </div>

      <!--
        Empty-state welcome surface.

        Three branches share the same ``v-if`` chain (the empty state pre-message), so
        flipping ``chatMode`` swaps **only** between the docked greeting and the
        fullscreen welcome. ``hasMessages`` / ``streamError`` are the only triggers that
        unmount the welcome in favour of the message list — chatMode never does. This
        keeps the chat surface change "no remount" symmetric with the message list path,
        so the underlying ``useChatStream('sidepanel')`` subscription is unaffected.

        Why two welcome components instead of one parameterised one:
        - :class:`SidePanelChatGreeting` lays out for a ~380px-wide column (left-aligned
          gradient headline + four narrow template chips stacked vertically).
        - :class:`FullscreenWelcome` lays out for the full viewport (centered headline +
          horizontal quick-action chip row), and is the same component the dedicated
          ``/app/ai-assistant`` route renders, so the two surfaces stay visually aligned.

        The composer slot inside the welcome wrapper is the **teleport target** for
        :class:`SidePanelChatInput` while ``overlayWelcomeActive`` is true. The Teleport
        below ``ChatMessageList`` keeps the same composer **component instance** but
        moves its DOM into this slot — preserving typed text, the model-selector dropdown
        state, the "+" popover, the document badges, and the ``inputRef`` binding across
        the layout swap. That's the only safe way to lift the composer up to sit
        immediately under the welcome (matching :class:`AiChatMain`'s ``centerAiAssistantComposer``
        block) without remounting it on every empty ↔ messages transition.
      -->
      <div
        v-if="overlayWelcomeActive"
        class="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-4 py-8 sm:gap-8 sm:px-6 sm:py-10"
        data-cy="ai-side-panel-overlay-welcome"
      >
        <FullscreenWelcome
          class="w-full max-w-4xl"
          :first-name="firstName"
          :last-name="lastName"
          :show-create-document-quick-action="topTemplates.length >= 1"
          :show-todo-from-case-quick-action="topTemplates.length >= 2"
          :show-show-all-quick-action="allTemplates.length > 0"
          :create-document-quick-action-prompt="topTemplates[0]?.prompt"
          :todo-from-case-quick-action-prompt="topTemplates[1]?.prompt"
          @send-suggestion="onFullscreenWelcomeSuggestion"
          @show-all="onShowAll"
        />
        <div
          ref="overlayComposerSlotEl"
          class="flex w-full max-w-4xl flex-col"
          data-cy="ai-side-panel-overlay-composer-slot"
        />
      </div>
      <SidePanelChatGreeting
        v-if="!overlayWelcomeActive && !chat.hasMessages.value && !chat.streamError.value"
        :first-name="firstName"
        :last-name="lastName"
        :templates="topTemplates"
        :show-all-label="t('showAll')"
        data-cy="ai-side-panel-greeting"
        @select="onSuggestion"
        @show-all="onShowAll"
      />
      <ChatMessageList
        v-if="!overlayWelcomeActive && (chat.hasMessages.value || chat.streamError.value)"
        :messages="chat.messages.value"
        :error="chat.streamError.value"
        :session-uuid="chat.activeSession.value?.uuid"
        :streaming-label="streamingLabel"
        :centered-content="isFullscreenOverlay"
        data-cy="ai-side-panel-messages"
        @retry="chat.retryLastMessage()"
        @open-preferences-dialog="headerRef?.openModalWithTab('settings')"
      />

      <!--
        ``<Teleport>`` keeps a single :class:`SidePanelChatInput` instance and moves
        its DOM between two anchors:

        - **Disabled (default)**: composer renders in source order — i.e. at the bottom
          of the panel, just below the greeting / message list. This is the layout for
          docked mode and for overlay-with-messages.
        - **Enabled (overlay + empty)**: composer renders inside ``#ai-side-panel-overlay-composer-slot``
          (a flex sibling of :class:`FullscreenWelcome` inside the centered welcome
          wrapper above), so the welcome and composer read as a single centered block.

        Because Vue keeps the same component instance across the move, ``inputRef``,
        the model selector's ``v-model``, the popover's ``open`` state, any typed text,
        and any in-flight document uploads all survive the layout swap. A ``watch`` on
        :ref:`overlayWelcomeActive` restores keyboard focus after each move because
        native ``appendChild`` reparenting drops focus from the textarea.
      -->
      <Teleport
        :to="overlayComposerSlotEl"
        :disabled="!overlayWelcomeActive || !overlayComposerSlotEl"
      >
        <SidePanelChatInput
          ref="inputRef"
          :disabled="chat.isStreaming.value"
          :court-database-flag-enabled="courtDatabaseFlag.isEnabled.value"
          :court-db-enabled="courtDbEnabled"
          :badges="inputBadges"
          :uploads-in-progress="uploadsInProgress"
          :selected-case="
            activeCaseLabel ? { uuid: manualCase?.uuid ?? null, label: activeCaseLabel } : null
          "
          :case-locked="caseLocked"
          :model-id="selectedModelId"
          :available-models="unifiedChat.availableModels"
          :chat-context="toChatContext(AIChatSessionTypeEnum.UNIFIED, unifiedChat.hasContextCase)"
          :class="isFullscreenOverlay ? 'mx-auto w-full max-w-4xl' : ''"
          data-cy="ai-side-panel-input"
          @send="onSend"
          @open-prompts="onOpenPrompts"
          @toggle-court-db="toggleCourtDb"
          @upload-documents="onUploadDocuments"
          @select-case="onSelectCase"
          @remove-case="onRemoveCase"
          @remove-badge="onRemoveBadge"
          @update:model-id="selectedModelId = $event"
        />
      </Teleport>
    </div>

    <AiAssistantBetaSidePanelView
      v-if="activeTab === 'ai_assistant'"
      @close="onClose"
    />

    <SidePanelTabBar
      v-if="shouldShowSidePanelTab"
      v-model:active="activeTab"
    />

    <RenameChatDialog
      v-model:open="showRenameDialog"
      :current-title="chat.activeSession.value?.title"
      @confirm="onRenameConfirmed"
    />
    <DeleteChatDialog
      v-model:open="showDeleteDialog"
      @confirm="onConfirmDelete"
    />
  </SidePanel>
</template>
